import { NextGenUiToggle } from '@components/NextGenUiToggle';
import { useSessionDetails } from '@hooks/data/useSessionDetails';
import { useConfigContext } from '@fe-core';
import {
  Navigate,
  Route,
  RouteProps,
  Routes,
  useParams,
} from 'react-router-dom';
import { SessionDetailsPage } from './SessionDetailsPage';
import { OverviewTab } from '@components/tabs/overview/OverviewTab';
import { InstructorsTab } from '@components/tabs/InstructorsTab';
import { ParticipantsTab } from '@components/tabs/ParticipantsTab';
import { EventsTab } from '@components/tabs/events/EventsTab';
import { isProjectSelection } from '@fe-learner-common/api/schemas/sessionsSchema';

export type TTabKeys = 'overview' | 'participants' | 'instructors' | 'events';
type TTabRoute = RouteProps & {
  enabled?: boolean;
};

export const SessionDetailsRoute = () => {
  const { sessionId } = useParams();
  const { extension } = useConfigContext();
  const hiddenTabs = extension?.['fe-learner-catalog']?.hiddenTabs ?? [];

  const { data: sessionDetails } = useSessionDetails({
    enabled: true,
    sessionId,
  });

  if (!sessionDetails) {
    return null;
  }

  // A project selection has no course design, so it never has trainers or events.
  const isSelection = isProjectSelection(sessionDetails);

  const tabRoutes: Record<TTabKeys, TTabRoute> = {
    overview: {
      enabled: true,
      path: 'overview',
      element: <OverviewTab sessionDetails={sessionDetails} />,
    },
    instructors: {
      enabled: !isSelection,
      path: 'instructors',
      element: <InstructorsTab persons={sessionDetails.trainers} />,
    },
    participants: {
      enabled:
        sessionDetails.participantInfo?.enabled &&
        sessionDetails.participantInfo?.showThumbnail,
      path: 'participants',
      element: <ParticipantsTab persons={sessionDetails.participants} />,
    },
    events: {
      enabled: !isSelection,
      path: 'events',
      element: <EventsTab dates={sessionDetails.dates} />,
    },
  };

  const availableTabs = Object.entries(tabRoutes).filter(
    ([tab, route]) => route.enabled && !hiddenTabs.includes(tab),
  );
  const availableTabKeys = availableTabs.map(([tab]) => tab as TTabKeys);
  const [firstAvailableTabKey] = availableTabKeys;

  return (
    <Routes>
      <Route
        element={
          <>
            <NextGenUiToggle isLoading={false} />
            <SessionDetailsPage
              tabKeys={availableTabKeys}
              sessionDetails={sessionDetails}
            />
          </>
        }
      >
        {availableTabs.map(([tabKey, route]) => (
          <Route key={tabKey} {...route} />
        ))}
      </Route>
      <Route
        path="*"
        element={<Navigate to={firstAvailableTabKey} replace />}
      />
    </Routes>
  );
};
